import type { INeuroCommit, INeuroDocument } from 'neuro-data-structures';
import { omit } from 'Utility/ramdaReplacement';

import { getJWTData } from './jwtAuthTools';
import { arrayOfAll, dateFromPartial, hasAttributes, isPartialDate } from 'neuro-utils';
import { isDocumentType } from 'neuro-schemas';

const useruuid = getJWTData()?.useruuid;

export type TCommitControlProps = Pick<
  IControlProps,
  '_cid' | '_cdate' | '_editing' | '_commitsLength' | '_commitCreator' | '_commitSource'
>;

/**
 * Export data and control properties from latest selectable commit
 *
 * Note: Previously this required checking for user id, but document loading was changed so that
 *       unfinished commits from other users are filtered at loading time.
 * @param {INeuroCommit[]} commitArray - Array of commits
 * @return {object} Object of document data
 */
export const commitDataFromArray = (commitArray: INeuroCommit[]): TCommitControlProps | null => {
  let currentCommit: INeuroCommit | null = null;

  for (const commit of commitArray) {
    // Set the processed commit as currentCommit if there is no currentCommit, the processed commit is unfinished or
    // the the currentCommit is finished before the processed commit.
    if (
      !currentCommit ||
      commit.commitDate === null ||
      (currentCommit.commitDate !== null && currentCommit.commitDate < commit.commitDate)
    ) {
      currentCommit = commit;
    }
  }

  if (!currentCommit) return null;

  const returnCommit: TCommitControlProps = {
    ...(currentCommit.data as Record<string, TFieldValue>),
    _cid: currentCommit.commitId,
    _cdate: currentCommit.commitDate ?? currentCommit.createDate,
    _commitCreator: currentCommit.creatorId,
    _commitSource: currentCommit.source,
    _editing: currentCommit.commitDate === null,
    _commitsLength: commitArray.length,
  };

  return returnCommit;
};

/**
 * Return latest commit data
 * @param {INeuroDocument} doc - Document
 * @return {document} Document data with control props included
 */
export const mergeDocument = (doc: Partial<INeuroDocument>): IDocumentV2 | null => {
  if (!isDocumentType(doc.documentType)) return null;
  // Remove unfinished commits from other users
  const filteredCommitArray = doc.commits?.filter(c => !(c.commitDate === null && c.creatorId !== useruuid));

  const handledCommits = filteredCommitArray ? commitDataFromArray(filteredCommitArray) : undefined;

  if (!handledCommits)
    return {
      _id: doc.documentId,
      _type: doc.documentType,
      _lockedFor: doc.lockedFor,
      _source: doc.source,
      _creator: doc.creatorId,
      _creatorOrg: doc.creatorOrg,
      _ownerOrg: doc.ownerOrg,
      _docCreateDate: doc.createDate,
      _pastOwners: doc.pastOwners,
      _cdate: 9999999999999, // Can't be undefined since it's used in sorting documents all over the project, so use a large number so that it is sorted to the top if needed
    } as IDocumentV2;

  const returnData = {
    ...handledCommits,
    _id: doc.documentId,
    _type: doc.documentType,
    _creator: doc.creatorId,
    _creatorOrg: doc.creatorOrg,
    _ownerOrg: doc.ownerOrg,
    _lockedFor: doc.lockedFor,
    _source: doc.source,
    _docCreateDate: doc.createDate,
    _pastOwners: doc.pastOwners,
  } as IDocumentV2;

  return returnData;
};

/**
 * Get latest finished commit of all non-removed documents sorted by document
 * creation time (descending).
 *
 * @param {INeuroDocument[]} docArray - Array of documents
 * @return {document[]} Document data array with control props included
 */
export const sortAndMergeDocuments = (docArray: Array<INeuroDocument>): Array<IDocumentV2> => {
  if (!Array.isArray(docArray)) return [];
  return docArray
    .reduce((acc: Array<IDocumentV2>, cur) => {
      if (!Number.isInteger(cur.removeTS)) {
        const merged = mergeDocument(cur);
        if (merged) acc.push(merged);
      }
      return acc;
    }, [])
    .sort((a, b) => b._docCreateDate - a._docCreateDate);
};

export const controlProps = arrayOfAll.keys<IControlProps>()([
  '_id',
  '_type',
  '_cid',
  '_cdate',
  '_editing',
  '_lockedFor',
  '_creator',
  '_creatorOrg',
  '_ownerOrg',
  '_source',
  '_commitsLength',
  '_docCreateDate',
  '_commitCreator',
  '_commitSource',
  '_pastOwners',
]);

/**
 * Remove control props from document data
 * @param  {{ [key: string]: any }} doc - Document with control props
 * @return {{ [key: string]: any }} Any document without control props
 */
export const omitControlProps = <T extends object>(doc: T): Omit<T, keyof IControlProps> =>
  omit<T, keyof IControlProps>(controlProps, doc);

/**
 * Get array of fields missing in a document
 * @param {Array<string>} fields list of fields wanted to check in the data
 * @param {IFormData['document']} docData Data in which the fields are checked to be found
 * @returns Array of the fields missing, being undefined or null
 */
export const getMissingFields = (fields: Array<string>, docData: IFormData['document']): Array<string> => {
  const missing: Array<string> = [];
  fields.forEach(f => {
    if (!(f in docData) || docData[f] === undefined || docData[f] === null) {
      missing.push(f);
    }
  });
  return missing;
};

/**
 * Test document source field to determine if the document originated from integrations
 * @param source source field string
 * @returns Whether the source is integration
 */
export const sourceIsIntegration = (source: INeuroDocument['source']) => {
  const re = new RegExp(/integration/, 'i');
  return !!(source ?? '').match(re);
};

export const filterByDocumentType = <T extends TDocumentType>(
  docs: IDocumentV2[],
  type: T,
): IDocumentWithTypeV2<T>[] => {
  return docs.filter(d => d._type === type) as IDocumentWithTypeV2<T>[];
};

export const checkDocumentType = (docs: IDocumentV2[], id: string): TDocumentType | undefined => {
  return docs.find(d => d._id === id)?._type;
};

/**
 * Narrows form data to a single document type, for routing components that receive a union
 * of form data alongside the document being edited.
 *
 * FormEditingHandler builds the form data from the edited document's id, so the two always
 * correspond at runtime, but its types erase that link (children receive a bare `IFormData`).
 * This predicate asserts it back: the `_type` check proves nothing about `formData` to the
 * compiler, so the guarantee rests on FormEditingHandler pairing them correctly.
 *
 * @example
 * if (isFormDataFor('mobilityAid', document, formData)) return <AidForm formData={formData} />;
 */
export const isFormDataFor = <T extends TDocumentType>(
  type: T,
  document: { _type: TDocumentType } | undefined,
  formData: IFormData<any>,
): formData is IFormData<IDocumentWithTypeV2<T>> => document?._type === type;

/**
 * Narrows form data to match an already-narrowed document, for routing components that switch
 * on `document._type` and pass form data on to a type-specific child. The document type
 * parameter is inferred from `_document`, so a branch cannot be given a type it did not narrow to.
 *
 * Preferred over {@link isFormDataFor} wherever a narrowed document is in hand, since it keeps
 * the `switch` and needs no repeated type literal. See {@link isFormDataFor} for why the
 * assertion is needed at all.
 *
 * @example
 * switch (document._type) {
 *   case 'rulm':
 *     return <RULMForm formData={formDataByType(formData, document)} />;
 * }
 */
export const formDataByType = <T extends TDocumentType>(
  formData: IFormData<any>,
  _document: IDocumentWithTypeV2<T>,
): IFormData<IDocumentWithTypeV2<T>> => formData as IFormData<IDocumentWithTypeV2<T>>;

/**
 * Filters documents by type and returns a properly typed array.
 * The return type is inferred as a union of IDocumentWithTypeV2 for each provided type.
 *
 * @param documents - Array of documents to filter
 * @param types - Array of document types to filter by
 * @returns Filtered array with narrowed union type
 *
 * @example
 * const filtered = filterDocumentsByType(docs, ['bdi', 'bai']);
 * // Type: Array<IDocumentWithTypeV2<'bdi'> | IDocumentWithTypeV2<'bai'>>
 */
export const filterDocumentsByType = <const T extends readonly TDocumentType[]>(
  documents: IDocumentV2[],
  types: T,
): Array<Extract<IDocumentV2, { _type: T[number] }>> => {
  return documents.filter((doc): doc is Extract<IDocumentV2, { _type: T[number] }> => types.includes(doc._type));
};

/**
 * @note VOLATILE! Use with caution...
 * Could be developed further to a more generic solution that could be used more widely */
export const filterDocumentsByDateRequirement = (
  documents: IDocumentV2[],
): (IDocumentV2 & { date?: PartialDate | null } & TAnyObject)[] => {
  return documents.filter((doc): doc is IDocumentV2 & { date?: PartialDate | null } & TAnyObject => {
    return 'date' in doc;
  });
};

/** Function for sorting documents (of type `IDocumentV2`) in descending order (latest first) */
export const sortDocumentsDescending = (doc1: IDocumentV2, doc2: IDocumentV2): number => {
  let date1: Date | undefined;
  let date2: Date | undefined;

  if (hasAttributes(doc1, ['startDate']) && isPartialDate(doc1.startDate)) date1 = dateFromPartial(doc1.startDate);
  if (hasAttributes(doc1, ['endDate']) && isPartialDate(doc1.endDate)) date1 = dateFromPartial(doc1.endDate);
  if (hasAttributes(doc1, ['date']) && isPartialDate(doc1.date)) date1 = dateFromPartial(doc1.date);

  if (hasAttributes(doc2, ['startDate']) && isPartialDate(doc2.startDate)) date2 = dateFromPartial(doc2.startDate);
  if (hasAttributes(doc2, ['endDate']) && isPartialDate(doc2.endDate)) date2 = dateFromPartial(doc2.endDate);
  if (hasAttributes(doc2, ['date']) && isPartialDate(doc2.date)) date2 = dateFromPartial(doc2.date);

  date1 ??= new Date(doc1._cdate);
  date2 ??= new Date(doc2._cdate);

  return date1.getTime() > date2.getTime() ? -1 : date1.getTime() < date2.getTime() ? 1 : 0;
};

/** Function for sorting documents (of type `IDocumentV2`) in ascending order (earliest first) */
export const sortDocumentsAscending = (doc1: IDocumentV2, doc2: IDocumentV2): number => {
  let date1: Date | undefined;
  let date2: Date | undefined;

  if (hasAttributes(doc1, ['startDate']) && isPartialDate(doc1.startDate)) date1 = dateFromPartial(doc1.startDate);
  if (hasAttributes(doc1, ['endDate']) && isPartialDate(doc1.endDate)) date1 = dateFromPartial(doc1.endDate);
  if (hasAttributes(doc1, ['date']) && isPartialDate(doc1.date)) date1 = dateFromPartial(doc1.date);

  if (hasAttributes(doc2, ['startDate']) && isPartialDate(doc2.startDate)) date2 = dateFromPartial(doc2.startDate);
  if (hasAttributes(doc2, ['endDate']) && isPartialDate(doc2.endDate)) date2 = dateFromPartial(doc2.endDate);
  if (hasAttributes(doc2, ['date']) && isPartialDate(doc2.date)) date2 = dateFromPartial(doc2.date);

  date1 ??= new Date(doc1._cdate);
  date2 ??= new Date(doc2._cdate);

  return date1.getTime() > date2.getTime() ? 1 : date1.getTime() < date2.getTime() ? -1 : 0;
};

/** Maps documents to format used by many neuro-calculation-commons functions */
export const mapDocumentToNeuroDocument = (
  d: Omit<IDocumentV2, keyof IControlProps> & Partial<IControlProps>,
): INeuroDocWithData => ({
  documentId: d._id ?? '',
  documentType: d._type ?? '',
  creatorId: '',
  creatorOrg: '',
  createDate: d._docCreateDate ?? 0,
  ownerOrg: '',
  pastOwners: { '': 0 },
  removeTS: null,
  removeInfo: null,
  source: null,
  lockedFor: null,
  commits: [],
  latestCommit: {
    commitId: '',
    createDate: 0,
    creatorId: '',
    creatorOrg: '',
    commitDate: d._cdate ?? null,
    source: null,
    lockedFor: null,
    data: omitControlProps(d),
  },
});

export const isByInformant = (
  d: Omit<IDocumentV2, keyof IControlProps> & Partial<IControlProps>,
  informants: Array<IInformant>,
): boolean => {
  if (d._source === 'irom') return true;
  if (!d._creator) return false;

  return informants.some(inf => inf.mysqUserId === d._creator);
};

export const sortAndTakeLatest = <const T extends readonly TDocumentType[]>(
  types: T,
  documents: IDocumentV2[],
  howMany = 7,
): Array<Extract<IDocumentV2, { _type: T[number] }>> => {
  return filterDocumentsByType(documents, types).sort(sortDocumentsDescending).slice(0, howMany);
};
